import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { LoaderCircle, Package, X } from 'lucide-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { errorMessage } from '@/lib/error-message';

type Marketplace = {
  id: string;
  format: 'codex' | 'claude-code';
  repository: string;
  displayName: string;
};
type RequiredMarketplace = { repository: string; displayName: string };

const FORMAT_LABELS = { codex: 'Codex', 'claude-code': 'Claude Code' } as const;

function Row({ title, detail, aside }: { title: string; detail: string; aside: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-hairline border-b px-3.5 py-2.5 last:border-b-0">
      <Package aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{title}</span>
        <span className="block truncate font-mono text-muted-foreground text-xs">{detail}</span>
      </span>
      {aside}
    </div>
  );
}

/**
 * Marketplaces (D33b): the plugin sources of the catalog — Crowdex's, the organization's
 * required ones, and the GitHub repositories the user added.
 */
export function PluginMarketplacesDialog({
  open,
  onClose,
  onChange,
}: {
  open: boolean;
  onClose: () => void;
  onChange: () => void;
}) {
  const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
  const [required, setRequired] = useState<RequiredMarketplace[]>([]);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setMarketplaces(await apiFetch<Marketplace[]>('/api/plugins/marketplaces'));
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    void load().catch((reason: unknown) => setError(errorMessage(reason)));
    // The organization's required marketplaces, outside one: none.
    void apiFetch<{ policy: { requiredMarketplaces: RequiredMarketplace[] } }>(
      '/api/organizations/plugin-policy',
      { credentials: 'include' },
    ).then(
      (state) => setRequired(state.policy.requiredMarketplaces),
      () => setRequired([]),
    );
  }, [load, open]);

  async function change(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      onChange();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  function add(event: FormEvent) {
    event.preventDefault();
    const value = url.trim();
    if (!value) return;
    void change(async () => {
      await apiFetch('/api/plugins/marketplaces', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url: value }),
      });
      setUrl('');
    });
  }

  return (
    <Dialog onOpenChange={(next) => !next && onClose()} open={open}>
      <DialogContent
        className="rounded-2xl pointer-coarse:translate-none pointer-coarse:px-5! sm:max-w-lg"
        data-sheet
      >
        <DialogTitle>Marketplaces</DialogTitle>
        <DialogDescription>
          A GitHub repository with a Claude Code or Codex marketplace. Its plugins join the catalog;
          Claude Code hooks, commands and subagents are not imported.
        </DialogDescription>
        <form className="flex gap-2" onSubmit={add}>
          <Input
            aria-label="Marketplace repository URL"
            className="h-10 min-w-0 flex-1 rounded-xl px-3 font-mono text-sm"
            disabled={busy}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="github.com/owner/repository"
            value={url}
          />
          <button
            className={settingButtonClass('primary')}
            disabled={busy || url.trim() === ''}
            type="submit"
          >
            {busy && <LoaderCircle className="size-3.5 animate-spin" />}
            Add
          </button>
        </form>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <div className="overflow-hidden rounded-2xl bg-control/60">
          <Row
            aside={<span className="text-muted-foreground text-xs">Built in</span>}
            detail="Every member gets it"
            title="Crowdex catalog"
          />
          {required.map((marketplace) => (
            <Row
              aside={<span className="text-muted-foreground text-xs">Required by your org</span>}
              detail={marketplace.repository.replace(/^https:\/\//, '')}
              key={marketplace.repository}
              title={marketplace.displayName.replace(/^https:\/\/github\.com\//, '')}
            />
          ))}
          {marketplaces.map((marketplace) => (
            <Row
              aside={
                <button
                  aria-label={`Remove ${marketplace.displayName}`}
                  className="flex size-7 items-center justify-center rounded-control text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  disabled={busy}
                  onClick={() =>
                    void change(() =>
                      apiFetch('/api/plugins/marketplaces', {
                        method: 'DELETE',
                        headers: { 'content-type': 'application/json' },
                        body: JSON.stringify({ id: marketplace.id }),
                      }),
                    )
                  }
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              }
              detail={`${FORMAT_LABELS[marketplace.format]} · ${marketplace.repository.replace(/^https:\/\//, '')}`}
              key={marketplace.id}
              title={marketplace.displayName}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
